import { create } from 'zustand';
import { useGameStore } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';

/**
 * Annuaire des membres et photos de profil
 * (tasks/profils-messages-stats-plan.md). Chargé à l'entrée dans le campus,
 * rafraîchi à l'ouverture du panneau Personnes.
 */

export interface MemberEntry {
  userId: string;
  displayName: string;
  role: string;
  /** Planche Pixel Citizen : visage du personnage. */
  sheet: string | null;
  /** Empreinte de la photo de profil, null = pas de photo. */
  photoVersion: string | null;
  lastSeenAt: string | null;
  /** Profil d'accès (ex. Copycampers), null = tout le campus. */
  accessProfile?: { id: string; name: string } | null;
}

interface MembersState {
  members: Map<string, MemberEntry>;
  loadedAt: number;
  setMembers: (list: MemberEntry[]) => void;
  patchMember: (userId: string, patch: Partial<MemberEntry>) => void;
}

export const useMembersStore = create<MembersState>((set) => ({
  members: new Map(),
  loadedAt: 0,
  setMembers: (list) => set({ members: new Map(list.map((m) => [m.userId, m])), loadedAt: Date.now() }),
  patchMember: (userId, patch) => set((s) => {
    const current = s.members.get(userId);
    if (!current) return s;
    const members = new Map(s.members);
    members.set(userId, { ...current, ...patch });
    return { members };
  }),
}));

/** Identité de la session (JWT en mémoire). */
export function sessionInfo(): { jwt: string | null; tenantId: string; userId: string; role: string; isGuest: boolean } {
  const jwt = useGameStore.getState().jwt;
  try {
    const p = JSON.parse(atob(jwt?.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/') ?? '')) as { tid?: string; sub?: string; role?: string };
    const userId = p.sub ?? '';
    return { jwt, tenantId: p.tid ?? '', userId, role: (p.role ?? '').toUpperCase(), isGuest: userId.startsWith('guest:') };
  } catch {
    return { jwt, tenantId: '', userId: '', role: '', isGuest: true };
  }
}

export function apiHeaders(json = false): Record<string, string> {
  const { jwt, tenantId } = sessionInfo();
  return {
    Authorization: `Bearer ${jwt ?? ''}`,
    'X-Tenant-ID': tenantId,
    ...(json ? { 'Content-Type': 'application/json' } : {}),
  };
}

let loading: Promise<void> | null = null;

/** Charge l'annuaire (une requête à la fois ; `maxAgeMs` évite les rechargements rapprochés). */
export function loadMembers(maxAgeMs = 30_000): Promise<void> {
  if (Date.now() - useMembersStore.getState().loadedAt < maxAgeMs) return Promise.resolve();
  if (loading) return loading;
  loading = fetch(`${API_BASE}/world/members`, { headers: apiHeaders() })
    .then((r) => (r.ok ? r.json() : []))
    .then((list: MemberEntry[]) => { if (Array.isArray(list)) useMembersStore.getState().setMembers(list); })
    .catch((err) => console.warn('[members] annuaire indisponible:', err))
    .finally(() => { loading = null; });
  return loading;
}

// ── Photos : chargées en blob (route authentifiée), une URL objet par version ──

const photoUrls = new Map<string, Promise<string | null>>();

export function photoObjectUrl(userId: string, version: string): Promise<string | null> {
  const key = `${userId}:${version}`;
  let p = photoUrls.get(key);
  if (!p) {
    p = fetch(`${API_BASE}/world/members/${encodeURIComponent(userId)}/photo?v=${version}`, { headers: apiHeaders() })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => (b ? URL.createObjectURL(b) : null))
      .catch(() => null);
    photoUrls.set(key, p);
  }
  return p;
}

/** Lit un fichier image en data URL (envoi de photo). */
export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export const PHOTO_MAX_BYTES = 8 * 1024 * 1024;

export function photoErrorMessage(code: string | undefined, status: number): string {
  if (code === 'INVALID_PHOTO') return 'Cette image n’est pas lisible (formats acceptés : JPG, PNG, WebP, HEIC ; 8 Mo maximum).';
  if (status === 413) return 'Image trop lourde (8 Mo maximum).';
  return `La photo n’a pas pu être enregistrée (HTTP ${status}).`;
}
